import type { Vec3 } from '../hs1/vec'
import { JOINTS } from '../hs1/joints'
import { pointFor } from '../hs1/model'
import type { AxisFreedom, DofKey, HumanoidModel, PointKey } from '../hs1/model'
import type { OffSet, OffState } from '../hs1/state'

/**
 * Der Achsbaum — Ebene 1 des Modells.
 *
 * WAS ER ERSETZT UND WARUM. Heute liegt die Geometrie als PUNKTSATZ vor: sechzehn
 * Punkte, jeder mit einer absoluten Koordinate vom Kreuzgelenk aus. Das ist
 * bequem zu lesen und falsch zu bearbeiten. Wer den Oberschenkel verlängert,
 * muss Knie, Sprunggelenk, Zehengrundgelenk und Sohlenpunkt von Hand nachziehen
 * — vier Zahlen für eine Absicht, und wer eine vergisst, bekommt ein Bein mit
 * einem zu kurzen Schienbein, ohne dass es irgendwo auffällt.
 *
 * Im Baum hängt jeder Knoten an einem anderen und trägt seine Lage RELATIV dazu.
 * Der Oberschenkel ist dann eine Zahl, und alles darunter folgt.
 *
 * WAS ER NICHT ENTHÄLT: keinen Motor, kein Getriebe, keine Masse, keine
 * Hüllkurve. Das ist Ebene 2 und liegt je Gelenk in einer eigenen Baugruppe. Ein
 * Baum, der beides führt, wäre die Stelle, an der die Beinlänge davon abhängt,
 * welchen Motor jemand gewählt hat.
 *
 * DIE REGELN DES RECHENKERNS GELTEN AUCH HIER: keine Zeit, kein Zufall, kein
 * DOM. Gleiche Eingaben, gleiches Ergebnis.
 *
 * EINHEIT IST DER MILLIMETER, wie im Punktsatz. Das ist Absicht und keine
 * Schlamperei: dieser Baum soll den Punktsatz ERSETZEN, und solange beide
 * nebeneinander laufen, muss ein Test sie Zahl für Zahl vergleichen können. Ein
 * Einheitenwechsel an dieser Stelle wäre eine Umrechnung mitten in der Prüfung,
 * die die Prüfung wertlos macht. Die Umrechnung auf Meter passiert weiter unten
 * in `buildSkeleton`, genau wie bisher.
 */

/** Welche Art von Knoten. */
export type NodeKind =
  /** Trägt eine Winkelfreiheit — ein echtes Gelenk. */
  | 'gelenk'
  /**
   * Ein reiner Anschlusspunkt ohne Freiheit.
   *
   * Becken, Brustflansch, Sohlenpunkt. Sie bewegen sich nicht gegeneinander und
   * sind trotzdem nötig: an ihnen hängen Ketten, und ihre Lage ist eine
   * Konstruktionsentscheidung.
   */
  | 'anschluss'
  /**
   * Gehört zur MECHANIK eines Gelenks und nicht zum Skelett.
   *
   * Kurbelachse und Anlenkpunkt des Rumpfaktuators sind keine Knickpunkte der
   * Kette — sie sind Bauteile der Baugruppe, die das Kreuzgelenk antreibt. Sie
   * stehen hier, weil der heutige Punktsatz sie führt und dieser Baum ihn Zahl
   * für Zahl abbilden muss. Mit Ebene 2 ziehen sie in die Baugruppe um; die
   * Kennzeichnung sagt genau, welche das sind.
   */
  | 'mechanik'

export type RigState = 'entwurf' | 'ausgelegt' | 'konstruiert' | 'fest'

/** Die drei Freiheiten eines Knotens. `null` heisst: gesperrt, also keine Achse. */
export interface Freedom {
  /** Rollen, um die Längsachse. */
  x: AxisFreedom | null
  /** Nicken, um die Querachse. */
  y: AxisFreedom | null
  /** Gieren, um die Hochachse. */
  z: AxisFreedom | null
}

/**
 * Ausgeschaltet — nicht gelöscht.
 *
 * Wer wissen will, was eine Achse beiträgt, nimmt sie heraus und schaut. Sie
 * dafür zu löschen hiesse, sie danach mit allen Zahlen wieder aufzubauen.
 *
 * Der Zustand selbst liegt im Simulatorzustand und nicht im Baum — er gehört
 * zum Szenario und nicht zur Maschine. Hier steht nur, wie der Baum ihn sieht.
 */
export type Off = OffState

export interface RigNode {
  /** Kennung, stabil über jede Umbenennung. */
  id: string
  label: string
  kind: NodeKind
  /** Woran er hängt. `null` nur bei der Wurzel. */
  parent: string | null
  /** Lage RELATIV zum Elternknoten, mm. */
  at: Vec3
  /**
   * Die eigene Lage der LINKEN Seite — `null`, wenn beide Seiten gleich sind.
   *
   * Der Normalfall ist `null`. Eine gewollte Unsymmetrie ist eine
   * Konstruktionsentscheidung und keine Nebenwirkung; siehe `Asymmetry` im
   * Modell.
   */
  atLeft: Vec3 | null
  /** Gibt es ihn links wie rechts? */
  mirrored: boolean
  freedom: Freedom
  /**
   * Rolle im Gangmodell — der Schlüssel zur Normkurve.
   *
   * `null` heisst: dieser Knoten hat keine aufgezeichnete Kurve. Er wiegt, er
   * trägt, er braucht einen Antrieb, wenn er eine Freiheit hat — aber der
   * Referenzgang kennt ihn nicht. Ohne diesen Griff wäre entweder die Achszahl
   * weiter fest oder der aufgezeichnete Gang wäre weg.
   */
  role: string | null
  off: Off | null
  /** Das Fachkapitel, in dem diese Achse ausgearbeitet ist. */
  doc: string | null
  state: RigState
}

export interface Rig {
  nodes: RigNode[]
  /** Zugriff über die Kennung. */
  byId: Record<string, RigNode>
  /** Kennungen von der Wurzel abwärts — Eltern stehen immer vor ihren Kindern. */
  order: string[]
  /** Die Wurzel. */
  root: string
}

/* -------------------------------------------------------------------------
   Der heutige Stand als Baum
   ------------------------------------------------------------------------- */

/**
 * Woran jeder Punkt des heutigen Satzes hängt.
 *
 * DIE EINE TABELLE, DIE DER PUNKTSATZ NICHT HAT. Er führt sechzehn absolute
 * Koordinaten und verschweigt, welcher Punkt von welchem abhängt — das steht
 * heute nur in der Bühnenzeichnung und im Kopf derer, die sie gelesen haben.
 * Hier steht es als Daten, und ab hier folgt die Geometrie daraus.
 */
const PARENT: Record<PointKey, PointKey | null> = {
  cross: null,

  pelvis: 'cross',
  chest: 'cross',

  /* Die Mechanik des Rumpfaktuators. Zwei Drehantriebe im Becken wirken über
     Kurbeln und Schubstangen auf die Plattform über dem Kreuzgelenk. Sie sind
     keine Knickpunkte der Kette — siehe `NodeKind`. */
  waistD: 'pelvis',
  waistC: 'waistD',

  hipG: 'pelvis',
  hipR: 'hipG',
  hipN: 'hipR',
  knee: 'hipN',
  ankle: 'knee',
  mtp: 'ankle',
  sole: 'ankle',

  shoulder: 'chest',
  elbow: 'shoulder',
  wrist: 'elbow',

  headC: 'chest',
}

/** Was es links wie rechts gibt. */
const MIRRORED = new Set<PointKey>([
  'hipG',
  'hipR',
  'hipN',
  'knee',
  'ankle',
  'mtp',
  'sole',
  'shoulder',
  'elbow',
  'wrist',
  'waistD',
  'waistC',
])

/** Was zur Mechanik gehört und nicht zum Skelett. */
const MECHANISM = new Set<PointKey>(['waistD', 'waistC'])

/**
 * Welche Achse an welche Normkurve gebunden ist.
 *
 * Die Kurven liegen unter den Namen des Gangmodells — `hip`, `knee`, `ankle`.
 * Die Punkte tragen die Namen der Konstruktion — `hipN` für die Nickachse.
 * Diese Tabelle ist die Übersetzung, und sie ist bewusst UNVOLLSTÄNDIG: `hipG`
 * und `hipR` haben keine eigene Kurve, weil der aufgezeichnete Gang die Hüfte
 * als ein Gelenk mit drei Ebenen führt und nicht als drei Gelenke.
 */
const ROLE: Partial<Record<PointKey, string>> = {
  cross: 'lumbar',
  hipN: 'hip',
  knee: 'knee',
  ankle: 'ankle',
  mtp: 'mtp',
  shoulder: 'shoulder',
  elbow: 'elbow',
  headC: 'neck',
}

/** Welches Fachkapitel eine Achse ausarbeitet. */
const DOC: Partial<Record<PointKey, string>> = {
  knee: 'p00',
}

const EMPTY: Freedom = { x: null, y: null, z: null }

/**
 * Baut den Baum aus dem heutigen Modell.
 *
 * DAS IST DER SCHRITT, DER DIE UMSTELLUNG PRÜFBAR MACHT. Solange der Baum aus
 * dem Punktsatz entsteht, lässt sich beides nebeneinanderlegen: `worldPoints()`
 * muss denselben Satz zurückgeben, von dem es ausgegangen ist — Zahl für Zahl,
 * ohne Toleranz. Erst wenn das steht, kann der Punktsatz gehen.
 *
 * Rein: gleiche Eingaben, gleiches Ergebnis.
 */
export function rigFromModel(model: HumanoidModel, off: OffSet = {}): Rig {
  const points = model.points
  const nodes: RigNode[] = []

  for (const key of Object.keys(PARENT) as PointKey[]) {
    const point = points[key]
    if (!point) continue

    const parent = PARENT[key]
    const base = parent ? points[parent] : null

    const dofKey = point.dof
    const dof = dofKey ? model.dofs[dofKey as DofKey] : undefined

    const role = ROLE[key] ?? null

    nodes.push({
      id: key,
      label: point.label,
      kind: MECHANISM.has(key) ? 'mechanik' : dof ? 'gelenk' : 'anschluss',
      parent,
      /* Die Differenz zum Elternknoten — der ganze Unterschied zum Punktsatz. */
      at: base
        ? { x: point.x - base.x, y: point.y - base.y, z: point.z - base.z }
        : { x: point.x, y: point.y, z: point.z },
      /* Nur dort, wo links wirklich etwas anderes steht — sonst führte der Baum
         zwei Zahlensätze, von denen einer immer die Kopie des anderen ist. */
      atLeft: leftOffset(model, key, parent),
      mirrored: MIRRORED.has(key),
      freedom: dof ? { x: dof.x, y: dof.y, z: dof.z } : EMPTY,
      /* Eine Rolle nur, wenn es die Kurve auch gibt. Ein Verweis auf eine
         Aufzeichnung, die nicht existiert, wäre schlimmer als keiner. */
      role: role && JOINTS[role] ? role : null,
      /* Stillgelegt wird die WINKELFREIHEIT, nicht der Punkt: die Hüfte besteht
         aus drei Modulen und lässt sich einzeln in Gieren, Rollen und Nicken
         festsetzen. Ein Punkt ohne Freiheit kann nicht stillstehen — er steht
         ohnehin. */
      off: (dofKey ? off[dofKey] : undefined) ?? null,
      doc: DOC[key] ?? null,
      /* Der heutige Stand ist gerechnet und belegt, aber nicht festgeschrieben:
         festgeschrieben wird erst, wenn jede Achse ihre Mechanik hat. */
      state: 'ausgelegt',
    })
  }

  return indexRig(nodes)
}

/**
 * Setzt Verzeichnis und Reihenfolge über eine Knotenliste.
 *
 * DIE REIHENFOLGE IST DAS EIGENTLICHE ERGEBNIS. Wer den Baum durchrechnet —
 * Weltlagen, Momente vom Fuss aufwärts, Trägheiten nach oben — braucht die
 * Gewissheit, dass ein Elternknoten fertig ist, bevor sein Kind an die Reihe
 * kommt. Sie hier einmal zu bilden ist billiger und sicherer, als in jeder
 * Rechnung erneut zu suchen.
 */
export function indexRig(nodes: RigNode[]): Rig {
  const byId: Record<string, RigNode> = {}
  for (const node of nodes) byId[node.id] = node

  const roots = nodes.filter((n) => n.parent === null)
  if (roots.length !== 1) {
    throw new Error(`Der Achsbaum braucht genau eine Wurzel, gefunden: ${roots.length}`)
  }

  const order: string[] = []
  const seen = new Set<string>()
  const children = new Map<string, RigNode[]>()
  for (const node of nodes) {
    if (node.parent === null) continue
    if (!byId[node.parent]) {
      throw new Error(`Knoten ${node.id} hängt an ${node.parent}, den es nicht gibt`)
    }
    const list = children.get(node.parent)
    if (list) list.push(node)
    else children.set(node.parent, [node])
  }

  const walk = (node: RigNode) => {
    if (seen.has(node.id)) throw new Error(`Der Achsbaum hat einen Kreis bei ${node.id}`)
    seen.add(node.id)
    order.push(node.id)
    for (const child of children.get(node.id) ?? []) walk(child)
  }
  walk(roots[0])

  if (order.length !== nodes.length) {
    const lost = nodes.filter((n) => !seen.has(n.id)).map((n) => n.id)
    throw new Error(`Nicht am Baum: ${lost.join(', ')}`)
  }

  return { nodes, byId, order, root: roots[0].id }
}

/* -------------------------------------------------------------------------
   Abgeleitete Grössen
   ------------------------------------------------------------------------- */

/**
 * Die absolute Lage jedes Knotens, mm.
 *
 * Die Umkehrung von `rigFromModel`. Zusammen sind die beiden der Beweis, dass
 * der Baum dieselbe Maschine beschreibt wie der Punktsatz.
 *
 * OHNE DREHUNGEN. Dies ist die GRUNDSTELLUNG — die Lage, in der der Punktsatz
 * bemasst ist. Wo die Knoten stehen, wenn Gelenke gedreht sind, rechnet die
 * Kinematik; das ist eine andere Frage und gehört nicht hierher.
 */
export function worldPoints(rig: Rig, side: RigSide = 'R'): Record<string, Vec3> {
  const out: Record<string, Vec3> = {}
  for (const id of rig.order) {
    const node = rig.byId[id]
    const base = node.parent ? out[node.parent] : { x: 0, y: 0, z: 0 }
    /* Die linke Seite nimmt ihre eigene Lage, wo es eine gibt. Da `at` relativ
       ist, trägt sich der Unterschied von selbst durch die ganze Kette weiter. */
    const at = side === 'L' ? (node.atLeft ?? node.at) : node.at
    out[id] = {
      x: base.x + at.x,
      y: base.y + at.y,
      z: base.z + at.z,
    }
  }
  return out
}

/** Welche Körperseite. */
export type RigSide = 'R' | 'L'

/**
 * Die eigene Lage der linken Seite, relativ zum Elternknoten.
 *
 * `null`, wenn weder der Punkt noch sein Elternknoten links etwas Eigenes
 * trägt. Der Elternknoten zählt mit: verschiebt sich links die Hüftnickachse,
 * ändert sich damit auch der Abstand zum Knie darunter.
 */
function leftOffset(
  model: HumanoidModel,
  key: PointKey,
  parent: PointKey | null,
): Vec3 | null {
  const own = model.asym?.[key]
  const parentOwn = parent ? model.asym?.[parent] : undefined
  if (!own && !parentOwn) return null

  const point = pointFor(model, key, 'L')
  const base = parent ? pointFor(model, parent, 'L') : null
  return base
    ? { x: point.x - base.x, y: point.y - base.y, z: point.z - base.z }
    : { x: point.x, y: point.y, z: point.z }
}

/** Trägt dieser Knoten eine gewollte Unsymmetrie? */
export const nodeAsym = (node: RigNode): boolean => node.atLeft !== null

/** Der Weg von der Wurzel zu einem Knoten, einschliesslich beider. */
export function chain(rig: Rig, id: string): string[] {
  const out: string[] = []
  let at: string | null = id
  while (at) {
    const node: RigNode | undefined = rig.byId[at]
    if (!node) throw new Error(`Unbekannter Knoten: ${at}`)
    out.unshift(at)
    at = node.parent
  }
  return out
}

/** Die Kinder eines Knotens, in der Reihenfolge des Baums. */
export function childrenOf(rig: Rig, id: string): RigNode[] {
  return rig.nodes.filter((n) => n.parent === id)
}

/**
 * Länge des Glieds zwischen einem Knoten und seinem Elternknoten, mm.
 *
 * Genau das, was im Punktsatz nirgends steht und überall gebraucht wird: die
 * Oberschenkellänge ist der Abstand von der Hüftnickachse zur Knieachse, und im
 * Punktsatz ist sie eine Differenz zweier absoluter Zahlen, die niemand als
 * Länge führt.
 */
export function segmentLength(rig: Rig, id: string): number {
  const node = rig.byId[id]
  if (!node) throw new Error(`Unbekannter Knoten: ${id}`)
  if (!node.parent) return 0
  const { x, y, z } = node.at
  return Math.hypot(x, y, z)
}

/** Abstand zweier Knoten in der Grundstellung, mm. */
export function distance(rig: Rig, a: string, b: string): number {
  const world = worldPoints(rig)
  const pa = world[a]
  const pb = world[b]
  if (!pa || !pb) throw new Error(`Unbekannter Knoten: ${!pa ? a : b}`)
  return Math.hypot(pa.x - pb.x, pa.y - pb.y, pa.z - pb.z)
}

/* -------------------------------------------------------------------------
   Achsen
   ------------------------------------------------------------------------- */

export const AXIS_KEYS = ['x', 'y', 'z'] as const
export type AxisKey = (typeof AXIS_KEYS)[number]

export interface Axis {
  node: string
  axis: AxisKey
  freedom: AxisFreedom
  /** Ausgeschaltet — die Achse steht auf ihrem Rastwinkel. */
  off: Off | null
}

/**
 * Jede Freiheit als eigene Zeile.
 *
 * DAS IST DIE ZAHL, UM DIE ES GEHT: die Achszahl des Roboters. Sie steht heute
 * als „achtundzwanzig" in Texten und nirgends als Rechnung. Hier fällt sie aus
 * dem Baum heraus — und wer eine Achse anlegt, sieht sie sich ändern.
 *
 * `mirrored` zählt DOPPELT: ein Knie gibt es links und rechts, und jedes
 * braucht seinen eigenen Antrieb. Wer die Achszahl aus den Knoten zählt statt
 * aus den Achsen, unterschlägt die halbe Maschine.
 */
export function axes(rig: Rig, options: { sides?: boolean } = {}): Axis[] {
  const withSides = options.sides ?? true
  const out: Axis[] = []
  for (const id of rig.order) {
    const node = rig.byId[id]
    const count = withSides && node.mirrored ? 2 : 1
    for (const axis of AXIS_KEYS) {
      const freedom = node.freedom[axis]
      if (!freedom) continue
      for (let i = 0; i < count; i++) out.push({ node: id, axis, freedom, off: node.off })
    }
  }
  return out
}

/** Wie viele Achsen die Maschine hat — ausgeschaltete nicht mitgezählt. */
export function axisCount(rig: Rig): number {
  return axes(rig).filter((a) => a.off === null).length
}

/* -------------------------------------------------------------------------
   Ändern
   ------------------------------------------------------------------------- */

/**
 * Gibt einen NEUEN Baum mit einem geänderten Knoten zurück.
 *
 * Wie überall im Rechenkern: der übergebene Baum wird nicht angefasst. Ein
 * Werkzeug, das seine Eingabe verändert, macht jede Gegenüberstellung von
 * „vorher" und „nachher" unmöglich — und Gegenüberstellungen sind der Zweck
 * dieses Baums.
 */
export function withNode(rig: Rig, id: string, patch: Partial<RigNode>): Rig {
  if (!rig.byId[id]) throw new Error(`Unbekannter Knoten: ${id}`)
  return indexRig(rig.nodes.map((n) => (n.id === id ? { ...n, ...patch, id: n.id } : n)))
}

/** Schaltet eine Achse aus oder wieder ein. */
export function setOff(rig: Rig, id: string, off: Off | null): Rig {
  return withNode(rig, id, { off })
}
